import React, { useEffect, useState } from 'react';
import {
  Button, Input, Popconfirm, Radio, Space, Upload, notification,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import zoneStore from '../../Store';
import selectionStore, { selectScreen } from '../../Store/selectionStore';
import defaultValues from '../../../utils/defaultValues';
import {
  findButton, findScreen, getScreens, replaceButton, replaceScreen, saveZone,
} from '../../../utils/zoneMutations';
import { readIconFile } from '../../../utils/buttonIcon';
import FieldLabel, { PanelTitle } from './FieldLabel';
import AdvancedBlock from './AdvancedBlock';
import ActionPicker from './ActionPicker';
import NewScreenMiniForm from './NewScreenMiniForm';
import NewQueueMiniForm from './NewQueueMiniForm';

function ButtonPanel() {
  const screens = zoneStore.useState((s) => s.config.screens);
  const { screenId, buttonId } = selectionStore.useState((s) => s);

  const screen = findScreen(screens, screenId);
  const button = findButton(screens, screenId, buttonId);

  const [formData, setFormData] = useState(button || {});
  const [creating, setCreating] = useState(null);

  useEffect(() => {
    setFormData(findButton(getScreens(), screenId, buttonId) || {});
    setCreating(null);
  }, [screenId, buttonId]);

  if (!screen || !button) return null;

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const apply = async () => {
    await saveZone(
      replaceButton(getScreens(), screenId, { ...button, ...formData }),
      'Кнопка сохранена',
    );
  };

  const deleteButton = async () => {
    const updated = {
      ...screen,
      buttons: screen.buttons.filter((item) => item.id !== buttonId),
    };
    const ok = await saveZone(replaceScreen(getScreens(), updated), 'Кнопка удалена');
    if (ok) selectScreen(screenId);
  };

  const handleUpload = async (info) => {
    const file = [...info.fileList].slice(-1)[0];
    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      notification.error({ message: 'Нужен PNG или JPEG' });
      return;
    }
    const icon = await readIconFile(file.originFileObj);
    setFormData((prev) => ({ ...prev, icon }));
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space>
        <Button
          size="small"
          icon={<ArrowLeftOutlined />}
          onClick={() => selectScreen(screenId)}
        />
        <PanelTitle>
          {`Кнопка экрана «${screen.headTitle || screen.screenId}»`}
        </PanelTitle>
      </Space>

      <div>
        <FieldLabel>Текст кнопки</FieldLabel>
        <Input name="name" value={formData.name || ''} onChange={handleInput} />
      </div>

      <div>
        <FieldLabel>Дополнительный текст</FieldLabel>
        <Input name="extraText" value={formData.extraText || ''} onChange={handleInput} />
      </div>

      {creating === 'screen' && (
        <NewScreenMiniForm
          screenId={screenId}
          button={{ ...button, ...formData }}
          onDone={(created) => {
            setFormData((prev) => ({
              ...prev,
              path: created.screenId,
              params: { name: 'queue', value: 0 },
            }));
            setCreating(null);
          }}
          onCancel={() => setCreating(null)}
        />
      )}

      {creating === 'queue' && (
        <NewQueueMiniForm
          screenId={screenId}
          button={{ ...button, ...formData }}
          onDone={(queueId) => {
            setFormData((prev) => ({
              ...prev,
              path: 'print',
              params: { name: 'queue', value: queueId },
            }));
            setCreating(null);
          }}
          onCancel={() => setCreating(null)}
        />
      )}

      {creating === null && (
        <ActionPicker
          screen={screen}
          value={formData.path}
          params={formData.params}
          nextScreen={formData.nextScreen}
          onChange={(patch) => setFormData((prev) => {
            const next = { ...prev, ...patch };
            // nextScreen существует только у внешней интеграции
            if (next.path !== 'externalIntegration') delete next.nextScreen;
            return next;
          })}
          onCreateScreen={() => setCreating('screen')}
          onCreateQueue={() => setCreating('queue')}
        />
      )}

      <AdvancedBlock>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div>
            <FieldLabel>Размер</FieldLabel>
            <Radio.Group
              options={defaultValues.buttonSizes}
              value={formData.size}
              optionType="button"
              buttonStyle="solid"
              onChange={(e) => setFormData((prev) => ({ ...prev, size: e.target.value }))}
            />
          </div>
          <div>
            <FieldLabel>Перевод текста кнопки</FieldLabel>
            <Input name="name_eng" value={formData.name_eng || ''} onChange={handleInput} />
          </div>
          <div>
            <FieldLabel>Перевод доп. текста</FieldLabel>
            <Input
              name="extraText_eng"
              value={formData.extraText_eng || ''}
              onChange={handleInput}
            />
          </div>
          <div>
            <FieldLabel>Иконка — можно перетащить картинку прямо на кнопку в схеме</FieldLabel>
            <Space align="start">
              <Upload
                listType="picture-card"
                showUploadList={false}
                beforeUpload={() => false}
                onChange={handleUpload}
              >
                {formData.icon
                  ? <img src={formData.icon} alt="Иконка" style={{ width: '100%' }} />
                  : 'Выбрать'}
              </Upload>
              {formData.icon && (
                <Button size="small" onClick={() => setFormData((prev) => ({ ...prev, icon: false }))}>
                  Убрать
                </Button>
              )}
            </Space>
          </div>
        </Space>
      </AdvancedBlock>

      <Space>
        <Button type="primary" onClick={apply} disabled={!formData.name}>Применить</Button>
        <Popconfirm
          title="Удалить кнопку?"
          onConfirm={deleteButton}
          okText="Удалить"
          cancelText="Отмена"
        >
          <Button danger>Удалить</Button>
        </Popconfirm>
      </Space>
    </Space>
  );
}

export default ButtonPanel;
